import type { DesignPreset } from './types';

export type PresetFolderNode =
	| { kind: 'folder'; name: string; path: string[]; children: PresetFolderNode[] }
	| { kind: 'preset'; preset: DesignPreset };

/** Folder nodes are a view of names; they never become persisted resources. */
export function getDesignPresetNameParts(name: string): string[] {
	return name
		.split('/')
		.map(part => part.trim())
		.filter(Boolean);
}

export function groupDesignPresets(presets: readonly DesignPreset[]): PresetFolderNode[] {
	const roots: PresetFolderNode[] = [];
	for (const preset of presets) {
		const parts = getDesignPresetNameParts(preset.name);
		let children = roots;
		for (let index = 0; index < parts.length - 1; index += 1) {
			const name = parts[index];
			let folder = children.find(
				(node): node is Extract<PresetFolderNode, { kind: 'folder' }> =>
					node.kind === 'folder' && node.name === name
			);
			if (!folder) {
				folder = { kind: 'folder', name, path: parts.slice(0, index + 1), children: [] };
				children.push(folder);
			}
			children = folder.children;
		}
		children.push({ kind: 'preset', preset });
	}
	return roots;
}

export function getDesignPresetFolderPaths(presets: readonly DesignPreset[]): string[][] {
	const paths: string[][] = [];
	const visit = (nodes: PresetFolderNode[]) => {
		for (const node of nodes) {
			if (node.kind === 'folder') {
				paths.push(node.path);
				visit(node.children);
			}
		}
	};
	visit(groupDesignPresets(presets));
	return paths;
}

export function moveDesignPresets(
	presets: readonly DesignPreset[],
	ids: readonly string[],
	folderPath: readonly string[]
): DesignPreset[] {
	const selected = new Set(ids);
	return presets.map(preset => {
		if (!selected.has(preset.id)) return preset;
		const parts = getDesignPresetNameParts(preset.name);
		const name = [...folderPath, parts[parts.length - 1]].join('/');
		return name === preset.name ? preset : { ...preset, name };
	});
}

export function renameDesignPresetFolder(
	presets: readonly DesignPreset[],
	folderPath: readonly string[],
	nextName: string
): DesignPreset[] {
	const name = nextName.trim();
	if (!folderPath.length || !name || name.includes('/')) throw new Error('A folder needs a single name.');
	return presets.map(preset => {
		const parts = getDesignPresetNameParts(preset.name);
		const inside = parts.length > folderPath.length && folderPath.every((part, index) => parts[index] === part);
		if (!inside) return preset;
		parts[folderPath.length - 1] = name;
		return { ...preset, name: parts.join('/') };
	});
}
